import { Search } from 'lucide-react'
import line from '#/assets/line.svg'
import TagPill from '#/components/common/TagPill'
import { Checkbox } from '#/components/ui/checkbox'
import { useCategories } from '../hooks/useCategories'
import { useTags } from '../hooks/useTags'
import { cn } from '#/lib/utils'

interface CoursesFilterProps {
  selectedCategory?: string
  onSelectCategory?: (categorySlug?: string) => void
  selectedTag?: string
  onSelectTag?: (tagSlug?: string) => void
  searchQuery?: string
  onSearchChange?: (query: string) => void
  className?: string
  showSearch?: boolean
  variant?: 'sidebar' | 'drawer'
}

export default function CoursesFilter({
  selectedCategory,
  onSelectCategory,
  selectedTag,
  onSelectTag,
  searchQuery = '',
  onSearchChange,
  className,
  showSearch = true,
  variant = 'sidebar',
}: CoursesFilterProps) {
  const { data: rawCategories } = useCategories()
  const { data: rawTags } = useTags()

  const categories = Array.isArray(rawCategories) ? rawCategories : []
  const tags = Array.isArray(rawTags) ? rawTags : []

  return (
    <div className={cn('flex w-full flex-col gap-6', className)}>
      {showSearch && (
        <div>
          <label className="flex items-center gap-3 rounded-[14px] border border-neutral-200 bg-white px-4 py-3 text-[14px] text-neutral-600 shadow-xs focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
            <Search className="h-4 w-4 text-neutral-400" />
            <input
              className="w-full bg-transparent text-[15px] text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
              placeholder="Search courses..."
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
          </label>
        </div>
      )}

      {/* Category filter */}
      <div
        className={cn(
          'rounded-[18px] bg-white p-6 shadow-xs border border-neutral-100',
          variant === 'drawer' && 'border-neutral-200/80 bg-white',
        )}
      >
        <div className="flex items-start flex-col gap-2.5">
          <h3 className="text-xl font-bold text-neutral-900">Category</h3>
          <img src={line} alt="Line" className="h-4 w-auto" />
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex cursor-pointer items-center gap-3 text-[15px] font-medium text-neutral-700 hover:text-neutral-900 transition-colors">
            <Checkbox
              className="size-5 border-neutral-300"
              checked={!selectedCategory}
              onCheckedChange={() => onSelectCategory?.(undefined)}
            />
            <span>All Categories</span>
          </label>
          {categories.map((cat) => (
            <label
              key={cat.id}
              className="flex cursor-pointer items-center gap-3 text-[15px] font-medium text-neutral-700 hover:text-neutral-900 transition-colors"
            >
              <Checkbox
                className="size-5 border-neutral-300"
                checked={selectedCategory === cat.slug}
                onCheckedChange={() =>
                  onSelectCategory?.(
                    selectedCategory === cat.slug ? undefined : cat.slug,
                  )
                }
              />
              <span>{cat.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Tag filter */}
      <div
        className={cn(
          'rounded-[18px] bg-white p-6 shadow-xs border border-neutral-100',
          variant === 'drawer' && 'border-neutral-200/80 bg-white',
        )}
      >
        <div className="flex items-start flex-col gap-2.5">
          <h3 className="text-xl font-bold text-neutral-900">Tag</h3>
          <img src={line} alt="Line" className="h-4 w-auto" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2.5">
          <TagPill
            label="All"
            active={!selectedTag}
            onClick={() => onSelectTag?.(undefined)}
          />
          {tags.map((tag) => (
            <TagPill
              key={tag.id}
              label={tag.name}
              active={selectedTag === tag.slug}
              onClick={() =>
                onSelectTag?.(selectedTag === tag.slug ? undefined : tag.slug)
              }
            />
          ))}
        </div>
      </div>
    </div>
  )
}
